import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export type ComboOption = { value: string; label: string };

/**
 * Controlled type-to-filter select. Shows the selected option's label, filters
 * the list as you type, writes the chosen `value` via onChange. Empty value =
 * nothing selected (clear button resets to "").
 *
 * The option list is rendered via a portal (fixed) so it is never clipped by an
 * ancestor's overflow/stacking context.
 */
export function Combobox({
  value,
  options,
  onChange,
  onQueryChange,
  placeholder,
  disabled,
  clearable = true,
  allowCustom = false,
  portalContainer,
}: {
  value: string;
  options: ComboOption[];
  onChange: (value: string) => void;
  /** Fires on every keystroke with the raw typed text (TASK-60) — lets a
   *  caller widen its `options` (e.g. fetch paused/completed entries too)
   *  once the operator starts searching. Optional; existing callers are
   *  unaffected. */
  onQueryChange?: (query: string) => void;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  /**
   * Typed text with no matching option commits as free text once the box
   * loses focus (default false — existing pick-from-list-only callers, e.g.
   * Google Ads/Meta account pickers, keep their exact prior behavior).
   */
  allowCustom?: boolean;
  /**
   * Element the option list portals into (TASK-80). Defaults to
   * `document.body` (all six pre-existing callers keep that, unaffected). A
   * caller rendering the Combobox inside a modal Radix dialog must pass the
   * dialog content's own DOM node instead — a modal `Dialog.Content`
   * disables pointer events everywhere outside itself, so a `document.body`
   * portal ends up unclickable there.
   */
  portalContainer?: Element | null;
}) {
  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? (allowCustom ? v : "");
  const [query, setQuery] = useState(labelFor(value));
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<{ left: number; top: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Outside-click closes+commits from inside a useEffect that only re-runs on
  // [value, options] — a ref keeps it reading the latest keystroke instead of
  // a stale `query` snapshot from whenever that effect last (re)attached.
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    setQuery(labelFor(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  const place = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ left: r.left, top: r.bottom + 4, width: r.width });
  };

  useEffect(() => {
    if (!open) return;
    place();
    const onMove = () => place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || listRef.current?.contains(t)) return;
      setOpen(false);
      const typed = queryRef.current.trim();
      if (allowCustom && typed !== value) onChange(typed);
      else setQuery(labelFor(value));
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options, allowCustom]);

  const q = query.toLowerCase().trim();
  const filtered = (
    q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options
  ).slice(0, 100);

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            onQueryChange?.(e.target.value);
          }}
          onFocus={() => setOpen(true)}
          className="h-9 w-full rounded-md border border-border-primary bg-bg-primary px-3 text-sm text-text-primary focus:border-border-focus focus:outline-none disabled:opacity-60"
        />
        {clearable && value && !disabled && (
          <button
            type="button"
            onClick={() => {
              onChange("");
              setQuery("");
            }}
            title="Auswahl löschen"
            className="inline-flex items-center rounded-md border border-border-primary bg-bg-secondary px-2 text-text-secondary hover:bg-bg-hover"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {open &&
        rect &&
        filtered.length > 0 &&
        createPortal(
          <ul
            ref={listRef}
            style={{ position: "fixed", left: rect.left, top: rect.top, width: rect.width, zIndex: 1000 }}
            className="max-h-60 overflow-auto rounded-md border border-border-primary bg-bg-secondary py-1 shadow-lg"
          >
            {filtered.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(o.value);
                    setQuery(o.label);
                    setOpen(false);
                  }}
                  className="block w-full truncate px-3 py-1.5 text-left text-sm text-text-primary hover:bg-bg-hover"
                >
                  {o.label}
                </button>
              </li>
            ))}
          </ul>,
          portalContainer ?? document.body,
        )}
    </div>
  );
}
